import React, {useState, useContext, useMemo, useEffect} from "react";
import { useIntl } from "react-intl";
import {
  QuotationDetailProps,
  QuotationDraftTableSchema,
} from "../types";
import MaterialTable, {Action} from "@material-table/core";
import TableStyle from "components/TableStyle";
import { tableIcons } from "@ui-components/TableIcons";
import { useHistory } from "react-router-dom";
import CountryContext from "../../../CountryContext";
import AuthenticationService from "../../../services/AuthenticationService";
import {getColumns} from "./lib/utilities";
import {OPTIONS} from "./lib/consts";
import {ResaleFirstDraftCreationModal} from "../ResaleFirstDraftCreationModal";
import {downloadBlob} from "../../commons/lib/utils";
import {useQuotations} from "../hooks/useQuotations";
import Spinner from "../../../ui-components/Spinner";


export const QuotationDraftsTable = ({
  quotation,
  baseDraftInfo
}: QuotationDetailProps) => {

  // context
  const { country: { code } } = useContext(CountryContext);

  // hooks
  const intl = useIntl();
  const history = useHistory();
  const userData = AuthenticationService.getUserData();
  const { downloadQuotationPdf } = useQuotations();

  // utilities
  const scopes = AuthenticationService.getUserScopes();
  const canDuplicateQuotationDraft = scopes.includes('quotation:create');

  // memos
  const columns = useMemo(() => getColumns(intl, quotation), []);

  // state
  const [data, setData]
    = useState(quotation.drafts?.map(draft => ({
      ...draft,
      loading: false,
    })) || []);

  useEffect(() => {
    setData(quotation.drafts?.map(draft => ({
      ...draft,
      loading: false,
    })) || []);
  }, [quotation.drafts]);

  const [showResaleFirstDraftCreationModal, setShowResaleFirstDraftCreationModal] = useState(false);

  return (
    <>
    <TableStyle>
      <MaterialTable<QuotationDraftTableSchema>
        title={intl.formatMessage({ id: 'history', defaultMessage: 'Storico' })}
        options={OPTIONS}
        columns={columns}
        data={data}
        actions={[
          (rowData) =>
            rowData.id === baseDraftInfo?.id && rowData.status === 'latest' &&
            ({
              icon: tableIcons.Edit,
              tooltip: intl.formatMessage({id: 'edit', defaultMessage: 'Modifica'}),
              onClick: () => {
                if ([userData.id, userData.account_user_id].includes(quotation.id_resale)
                  && baseDraftInfo?.message === 'latest_agent_draft') {
                  setShowResaleFirstDraftCreationModal(true)
                  return
                }
                if (!baseDraftInfo?.id)
                  return;
                history.push(`/${code}/quotations/${quotation.id}/drafts/new`);
              }
            } as Action<QuotationDraftTableSchema>),
          (rowData: QuotationDraftTableSchema) => ({
            icon: tableIcons.Eye,
            tooltip: intl.formatMessage({id: 'view', defaultMessage: 'Visualizza'}),
            onClick: async () => {
              history.push(`/${code}/quotations/${quotation.id}/drafts/${rowData.id}`)
            }
          } as Action<QuotationDraftTableSchema>),
          (rowData: QuotationDraftTableSchema) => !rowData.loading? ({
            icon: tableIcons.Download,
            tooltip: intl.formatMessage({id: 'download', defaultMessage: 'Scarica'}),
            onClick: async () => {
              setData(data.map(draft => draft.id === rowData.id ? {...draft, loading: true} : draft))
              const result: Blob = await downloadQuotationPdf(quotation.id, rowData.id);
              downloadBlob(
                result,
                intl.formatMessage({
                  id: "quotation_pdf_filename",
                  defaultMessage: `Preventivo ${quotation.id}.pdf`,
                })
              );
              setData(data.map(draft => draft.id === rowData.id ? {...draft, loading: false} : draft))
            }
          } as Action<QuotationDraftTableSchema>): ({
            icon: () => <Spinner styleType={"white"} />,
            tooltip: intl.formatMessage({id: 'download', defaultMessage: 'Scarica'}),
          } as Action<QuotationDraftTableSchema>),
          ...(
            canDuplicateQuotationDraft
              ? [
                (rowData: QuotationDraftTableSchema) => rowData.id_creator !== quotation.id_resale && ({
                  icon: tableIcons.DocumentDuplicate,
                  tooltip: intl.formatMessage({id: 'duplicate', defaultMessage: 'Duplica'}),
                  onClick: () => {
                    history.push(`/${code}/quotations/${quotation.id}/drafts/${rowData.id}/duplicate`)
                  }
                }) as Action<QuotationDraftTableSchema>
              ]
              : []
          ),
        ]}
        localization={{
          header: {actions: ''}
        }}
      />
    </TableStyle>
      {showResaleFirstDraftCreationModal &&
          <ResaleFirstDraftCreationModal
              onConfirm={() => {
                setShowResaleFirstDraftCreationModal(false);
                history.push(`/${code}/quotations/${quotation.id}/drafts/${baseDraftInfo?.id}`);
              }}
              onDismiss={() => setShowResaleFirstDraftCreationModal(false)}
          />
      }
    </>
  );
}